import store from "../index";
import { VuexModule, Module, Mutation, Action } from "vuex-class-modules";
import { getDoc, DocumentReference, updateDoc, QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import playersDataAccessService from "@/services/da/players.service";
import { Filter, FilterTypes } from "../types/players";
import moment from "moment";
import controlCommandsService from "@/services/da/control-commands.service";

export enum VIEW_MODES {
  LIST = 'list',
  GRID = 'grid',
  GRID_SNAPHOSTS = 'grid-snapshots'
}

function filterPlayers(player: any, filters: Filter[]) {
  const filterResult: boolean[] = [];

  filters.forEach((theFilter: Filter) => {
    if (theFilter.type == FilterTypes.MATCH) {

      const { Name, Serial, PrimePlayerName } = player;
      const fields = [Name, Serial];
      if (PrimePlayerName != undefined) {
        fields.push(PrimePlayerName)
      }

      filterResult.push(
        fields.map(fieldValue => fieldValue.toLowerCase()
          .includes(theFilter.value.toLowerCase()))
          .indexOf(true) >= 0
      );
    } else if (player.PrimePlayerId != undefined  && theFilter.type == FilterTypes.PRIME_PLAYER && (theFilter.value as string[]).length > 0) {
      filterResult.push((theFilter.value as string[]).indexOf(player.PrimePlayerId) >= 0);
    } else if (theFilter.type == FilterTypes.ONLINE && theFilter.value) {
      filterResult.push(player.Online);
    } else if (theFilter.type == FilterTypes.OFFLINE && theFilter.value) {
      filterResult.push(!player.Online);
    } else if (theFilter.type == FilterTypes.GROUP && (theFilter.value as string[]).length > 0) {
      filterResult.push((theFilter.value as string[]).indexOf(player.ReportedGroup?.Id) >= 0);
    } else if (player.Presentation != undefined && theFilter.type == FilterTypes.PRESENTATION && (theFilter.value as string[]).length > 0) {
      filterResult.push((theFilter.value as string[]).indexOf(player.Presentation?.Name) >= 0);
    } else if (theFilter.type == FilterTypes.DT_DISABLED) {
      filterResult.push(
        [player.ContentDownloadsStartTime, player.ContentDownloadsEndTime]
          .filter(v => [null, undefined].indexOf(v) >= 0).length > 0);
    }
  })
  return !filterResult.includes(false) && filterResult.length > 0;
}

const DeviceConnectionsPeriod = new Map([
  ["ThirtySeconds", 30],
  ["OneMinute", 60],
  ["FiveMinutes", 60*5],
  ["FifteenMinutes", 60*15],
  ["ThirtyMinutes", 60*30],
  ["OneHour", 3600],
  ["SixHours", 3600*6],
  ["TwelveHours", 3600*12],
  ["OneDay", 3600*24]
])

@Module
class PlayersModule extends VuexModule {

  cItem: any = null;
  data: any[] = [];

  cPage = 1;
  perPage = 10;
  filters: Filter[] = [];

  viewMode = VIEW_MODES.LIST

  get collection() {
    return this.data.map(device => {

      if (device.Serial) {
        const length = 30
        const NameTruncated = device.Name.slice(0, length) + (device.Name.length > length ? '...' : '')

        const period = DeviceConnectionsPeriod.get(device.HealthReportingPeriod)
        const Online = period != undefined ? (Date.now() - Date.parse(device.LastHeartbeatTime)) / 1000 < period * 3 : false

        const LastHeartbeat = moment(device.LastHeartbeatTime).fromNow()

        return {...device, NameTruncated, Online, LastHeartbeat}
      }
      return device
    });
  }

  get filtered() {
    
    if (this.filters.length > 0) {
      return this.collection.filter((player: any) => filterPlayers(player, this.filters))
    }
    return this.collection
  }

  get paginated() {
    return this.filtered.slice((this.cPage - 1) * this.perPage, this.cPage * this.perPage)
  }

  get online() {
    return this.collection.filter(player => player.Online).length
  }

  get offline() {
    return this.collection.filter(player => !player.Online).length
  }

  get primePlayers() {
    return this.data.filter(player => player.primePlayer == undefined);
  }

  get primePlayersList() {
    return this.data.filter(player => [player.id, undefined].indexOf(player.PrimePlayerId) >= 0)
  }

  @Action
  async load(docs: QueryDocumentSnapshot<DocumentData>[]) {
    const players = await playersDataAccessService.getPlayersCollection(docs) as Array<{[key: string]: any}>
    this.update(players);
  }

  @Action
  changePage(page: number) {
    this.setCPage(page);
  }

  @Action
  filter(filter: Filter) {

    if (typeof filter.value == 'object' && filter.value != null && Object.keys(filter.value).length < 1) {
      this.deleteFilter(filter.type);
    } else if (!filter.value) {
      this.deleteFilter(filter.type);
    } else {
      this.putFilter(filter);
    }
  }

  @Action
  async playerDetails(id: string) {
    const idx = this.data.findIndex(player => player.id == id);
    if (idx >= 0) {
      const player = this.data[idx];
      if (!player.primePlayerId) {
        player.mscreen = await playersDataAccessService.mScreenConfigGet(player.id);
      }

      if (player.vcsc != undefined) {
        const virtualClassesStateCodes = await getDoc((player.vcsc as DocumentReference));

        if (virtualClassesStateCodes.exists()) {
          player.virtualClassesStateCodes = virtualClassesStateCodes.data();
        }
      }
      this.setCItem(player);
    }
  }

  @Action
  async enableDownloadTimes(t: any[]) {

    let args: any = { ContentDownloadTimeDisabled: false } 

    if (t.length == 2) {
      const ContentDownloadsStartTime = 'PT' + (t[0].hours) + 'H' + (t[0].minutes > 0 ? t[0].minutes + 'M' : '')
      const ContentDownloadsEndTime = 'PT' + (t[1].hours) + 'H' + (t[1].minutes > 0 ? t[1].minutes + 'M' : '')

      args = {...args, ContentDownloadsStartTime, ContentDownloadsEndTime}
    }

    for (const player of this.filtered) {
      await updateDoc(player.ref, args)
    }
  }

  @Action
  async disableDownloadTimes() {
    const ContentDownloadTimeDisabled = true
    const ContentDownloadsStartTime = null
    const ContentDownloadsEndTime = null
    for (const player of this.filtered) {  
      await updateDoc(player.ref, { 
        ContentDownloadTimeDisabled,
        ContentDownloadsStartTime, 
        ContentDownloadsEndTime
      })
    }
  }

  @Action 
  async rebootCommand(id = '0') {
    
    if (id != '0') {
      await controlCommandsService.reboot([ id ]);
    } else {
      let idx = 0;
      const max = 100;
      let offset = 0;

      const deviceIds = this.filtered.map(device => device.Serial);

      while (offset <= deviceIds.length) {
        await controlCommandsService.reboot(
          deviceIds.slice(offset, offset + max));
        idx++;
        offset = idx * max;
      }
    }
  }

  @Action 
  async getLogs() {
    let idx = 0
    const max = 100
    let offset = 0

    const deviceIds = this.filtered.map(device => device.Serial)

    while (offset <= deviceIds.length) {
      await controlCommandsService.getLogs(
        deviceIds.slice(offset, offset+max))
      idx++
      offset = idx * max
    }
  }

  @Action 
  async takeSnapshotCommand() {
    let idx = 0
    const max = 100
    let offset = 0

    const deviceIds = this.filtered.map(device => device.Serial)

    while (offset <= deviceIds.length) {
      await controlCommandsService.takeSnapshot(
        deviceIds.slice(offset, offset+max))
      idx++
      offset = idx * max
    }
  }

  @Action
  viewModeUpdate(mode: VIEW_MODES) {
    this.setViewMode(mode)
  }

  @Action
  reset() {
    this.resetStates()
  }

  @Mutation
  deleteFilter(type: FilterTypes) {
    const idx = this.filters.findIndex((filter => filter.type == type))
    if (idx >= 0) {
      this.filters.splice(idx, 1)
    }
  }

  @Mutation
  putFilter(filter: Filter) {
    const idx = this.filters.findIndex((theFilter => theFilter.type == filter.type))
    if (idx >= 0) {
      this.filters.splice(idx, 1, filter)
    } else {
      this.filters.push(filter)
    }
  }

  @Mutation
  update(players: any[]) {
    this.data = players;
  }

  @Mutation
  setCItem(player: any) {
    this.cItem = player;
  }

  @Mutation
  setCPage(page: number) {
    this.cPage = page;
  }

  @Mutation
  setViewMode(mode: VIEW_MODES) {
    this.viewMode = mode;
  }

  @Mutation
  resetStates() {
    this.data = [];
    this.cPage = 1;
    this.filters = [];
  }
}
const name = "playersModule";

export const playersModule = new PlayersModule({
  store,
  name  
});